<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { Edit3, Tags } from '@lucide/vue'
import {
  Badge, Button, EmptyState, FormField, Input, Modal, PageHeader, Section, Spinner, Stat, Table, Toolbar, useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'

const api = inject('api')
if (!api) throw new Error('TagManagement: api provider is required')

const { t } = useI18n()
const toast = useToast()
const packs = ref([])
const query = ref('')
const loading = ref(true)
const error = ref('')
const selectedTag = ref('')
const editingPack = ref(null)
const tagDraft = ref('')
const saving = ref(false)

const tagRows = computed(() => {
  const usage = new Map()
  for (const pack of packs.value) {
    for (const tag of pack.tags) {
      const entry = usage.get(tag) || { id: tag, tag, packs: [] }
      entry.packs.push(pack)
      usage.set(tag, entry)
    }
  }
  return [...usage.values()]
    .map(entry => ({
      ...entry,
      usage_count: entry.packs.length,
      pack_titles: entry.packs.map(pack => pack.title).join('、'),
    }))
    .sort((left, right) => right.usage_count - left.usage_count || left.tag.localeCompare(right.tag))
})

const visibleRows = computed(() => {
  const needle = query.value.trim().toLocaleLowerCase()
  if (needle === '') return tagRows.value
  return tagRows.value.filter(row => `${row.tag} ${row.pack_titles}`.toLocaleLowerCase().includes(needle))
})

const untaggedCount = computed(() => packs.value.filter(pack => pack.tags.length === 0).length)
const selectedRow = computed(() => tagRows.value.find(row => row.tag === selectedTag.value) || null)
const columns = computed(() => [
  { key: 'tag', label: t('visualGameAdmin.tagManagement.table.tag'), width: '220px' },
  { key: 'usage_count', label: t('visualGameAdmin.tagManagement.table.usage'), width: '120px', align: 'right' },
  { key: 'pack_titles', label: t('visualGameAdmin.tagManagement.table.packs'), width: '560px', wrap: true },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '140px', align: 'right', fixed: 'right' },
])

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await api.visualGame.listPacks()
    packs.value = result.packs
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function selectTag(row) {
  selectedTag.value = row.tag
}

function openEditor(pack) {
  editingPack.value = pack
  tagDraft.value = pack.tags.join(', ')
}

function closeEditor() {
  editingPack.value = null
  tagDraft.value = ''
}

async function save() {
  const tags = [...new Set(tagDraft.value.split(',').map(tag => tag.trim()).filter(Boolean))]
  if (tags.length > 12) {
    toast.error(t('visualGameAdmin.tagManagement.tooManyTags'), { duration: 0 })
    return
  }
  saving.value = true
  try {
    const updated = await api.visualGame.updatePack({ id: editingPack.value.id, tags })
    packs.value = packs.value.map(pack => pack.id === updated.id ? updated : pack)
    if (selectedTag.value !== '' && !tagRows.value.some(row => row.tag === selectedTag.value)) selectedTag.value = ''
    closeEditor()
    toast.success(t('visualGameAdmin.tagManagement.saved'))
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="tag-page vga-page" data-insp-path="apps/visual-game-admin/src/pages/TagManagement.vue">
    <PageHeader :title="t('visualGameAdmin.app.tagManagement')" />

    <div class="tag-page__stats">
      <Stat :label="t('visualGameAdmin.tagManagement.stats.tags')" :value="tagRows.length" />
      <Stat :label="t('visualGameAdmin.tagManagement.stats.packs')" :value="packs.length" />
      <Stat :label="t('visualGameAdmin.tagManagement.stats.untagged')" :value="untaggedCount" />
    </div>

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.tagManagement.searchLabel') }}</label>
          <Input v-model="query" type="search" size="sm" :placeholder="t('visualGameAdmin.tagManagement.searchPlaceholder')" data-testid="tag-search" />
        </div>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <Section v-else :padded="false">
      <EmptyState v-if="visibleRows.length === 0" :title="t('visualGameAdmin.tagManagement.emptyTitle')" :description="t('visualGameAdmin.tagManagement.emptyDescription')" />
      <Table v-else :columns="columns" :rows="visibleRows" clickable @row-click="selectTag">
        <template #tag="{ row }"><Badge size="sm"><Tags :size="13" /> {{ row.tag }}</Badge></template>
        <template #usage_count="{ row }">{{ t('visualGameAdmin.tagManagement.usageCount', { count: row.usage_count }) }}</template>
        <template #actions="{ row }">
          <Button variant="secondary" size="sm" @click.stop="selectTag(row)">{{ t('visualGameAdmin.tagManagement.viewPacks') }}</Button>
        </template>
      </Table>
    </Section>

    <Section v-if="selectedRow" class="tag-page__detail">
      <div class="tag-page__detail-header">
        <div><span class="tag-page__eyebrow">{{ t('visualGameAdmin.tagManagement.detailLabel') }}</span><h2>{{ selectedRow.tag }}</h2></div>
        <span class="vga-muted">{{ t('visualGameAdmin.tagManagement.usageCount', { count: selectedRow.usage_count }) }}</span>
      </div>
      <div class="tag-page__pack-list">
        <div v-for="pack in selectedRow.packs" :key="pack.id" class="tag-page__pack-row">
          <div><strong>{{ pack.title }}</strong><small>{{ pack.series_title || pack.id }}</small></div>
          <div class="tag-page__pack-tags"><Badge v-for="tag in pack.tags" :key="tag" size="sm">{{ tag }}</Badge></div>
          <Button variant="ghost" size="sm" @click="openEditor(pack)"><Edit3 :size="14" /> {{ t('visualGameAdmin.tagManagement.editPack') }}</Button>
        </div>
      </div>
    </Section>

    <Modal :open="editingPack !== null" :title="t('visualGameAdmin.tagManagement.editTitle', { title: editingPack?.title || '' })" :close-on-backdrop="!saving" width="560px" @update:open="!$event && closeEditor()">
      <FormField :label="t('visualGameAdmin.tagManagement.tagsLabel')" :hint="t('visualGameAdmin.tagManagement.tagsHint')">
        <Input v-model="tagDraft" :disabled="saving" data-testid="tag-editor" @keyup.enter="save" />
      </FormField>
      <template #footer>
        <Button variant="ghost" :disabled="saving" @click="closeEditor">{{ t('visualGameAdmin.common.cancel') }}</Button>
        <Button :loading="saving" @click="save">{{ t('visualGameAdmin.common.save') }}</Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.tag-page__stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--drama-space-md); }
.tag-page__detail { display: grid; gap: var(--drama-space-lg); }
.tag-page__detail-header { display: flex; align-items: end; justify-content: space-between; gap: var(--drama-space-lg); }
.tag-page__detail-header h2 { margin: var(--drama-space-2xs) 0 0; font-family: var(--drama-font-family-heading); font-size: var(--drama-font-size-lg); }
.tag-page__eyebrow { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); font-weight: var(--drama-font-weight-medium); letter-spacing: .08em; text-transform: uppercase; }
.tag-page__pack-list { display: grid; border-top: 1px solid var(--drama-color-border); }
.tag-page__pack-row { display: grid; grid-template-columns: minmax(180px, .8fr) minmax(280px, 1.2fr) auto; align-items: center; gap: var(--drama-space-lg); padding: var(--drama-space-md) 0; border-bottom: 1px solid var(--drama-color-border); }
.tag-page__pack-row > div:first-child { display: grid; gap: var(--drama-space-2xs); }
.tag-page__pack-row small { color: var(--drama-color-text-muted); }
.tag-page__pack-tags { display: flex; flex-wrap: wrap; gap: var(--drama-space-xs); }
@media (max-width: 760px) { .tag-page__stats, .tag-page__pack-row { grid-template-columns: 1fr; } .tag-page__pack-row { align-items: stretch; } }
</style>
